import type { View } from '../router/router';
import { apiClient } from '../services/api-client';
import { restAlert } from '../services/rest-alert';
import { toast } from '../components/layout/toast';
import { confirmDialog } from '../components/layout/confirm-dialog';
import { getTyrisIcon } from '../components/ui/tyris-icon';

interface Exercise {
  id: string;
  name: string;
  description?: string;
  gifPath?: string | null;
  restSec?: number;
}

interface RoutineExercise {
  id: string;
  order: number;
  sets: number;
  reps: string;
  weight: number;
  rpe?: number;
  rir?: number;
  restSec?: number | null;
  exercise: Exercise;
}

interface ActiveRoutine {
  id: string;
  name: string;
  description?: string | null;
  exercises: RoutineExercise[];
}

interface LoggedSet {
  exerciseId: string;
  exerciseName: string;
  setNumber: number;
  weight: number;
  reps: number;
  rir?: number | null;
}

interface ExerciseReferenceSet {
  setNumber: number;
  weight: number;
  reps: number;
}

interface ExerciseReference {
  exerciseId: string;
  lastSession: {
    date: string;
    sets: ExerciseReferenceSet[];
  } | null;
  personalRecord: {
    date: string;
    weight: number;
    reps: number;
  } | null;
}

type Phase = 'idle' | 'active' | 'rest' | 'done';

export class WorkoutSessionPage implements View {
  private activeRoutines: ActiveRoutine[] = [];
  private allExercises: Exercise[] = [];
  private loggedSets: LoggedSet[] = [];
  /** Referencias históricas (última sesión + PR) por exerciseId */
  private referenceByExercise: Record<string, ExerciseReference> = {};
  private referenceLoadingByExercise: Record<string, boolean> = {};
  private referenceErrorByExercise: Record<string, boolean> = {};
  private lastSelectedExerciseId: string | null = null;
  /** Último peso y reps anotados por exerciseId, persistido en localStorage */
  private lastUsedByExercise: Record<string, { weight: number; reps: number }> = {};
  private phase: Phase = 'idle';
  private selectedRoutineId: string | null = null;
  private selectedRoutineExerciseId: string | null = null;
  private restRemaining = 0;
  /** Momento (epoch ms) en que termina el descanso actual, o null si no hay descanso activo */
  private restDeadline: number | null = null;
  private restInterval: ReturnType<typeof setInterval> | null = null;
  private notes = '';
  private startedAt: Date | null = null;
  private finishedAt: Date | null = null;
  private isSaving = false;

  constructor() {
    document.addEventListener('visibilitychange', this.handleAppResume);
    window.addEventListener('focus', this.handleAppResume);
    window.addEventListener('hashchange', this.handleRouteLeave);
  }

  private readonly handleAppResume = () => {
    if (document.hidden) return;
    if (this.phase !== 'rest' || this.restDeadline == null) return;
    this.restRemaining = this.getRemainingSeconds();
    if (this.restRemaining <= 0) {
      restAlert.playIfMissed(this.restDeadline);
      this.stopRest();
      return;
    }
    this.saveState();
    restAlert.schedule(this.restDeadline);
    const el = document.getElementById('ws-timer');
    if (el) el.textContent = this.formatTime(this.restRemaining);
  };

  private readonly handleRouteLeave = () => {
    const path = (window.location.hash || '').split('?')[0];
    if (path === '#/workout') return;
    restAlert.cancel();
    this.stopRestOnlyInterval();
  };

  render(): string {
    return `<div id="ws-root"></div>`;
  }

  async afterRender(): Promise<void> {
    await this.loadData();
    this.drawPhase();
  }

  private async loadData() {
    try {
      const [routinesResult, exercisesResult] = await Promise.allSettled([
        apiClient.get<ActiveRoutine[]>('/api/routines/my-active-list'),
        apiClient.get<Exercise[]>('/api/exercises'),
      ]);

      if (routinesResult.status === 'fulfilled' && Array.isArray(routinesResult.value)) {
        this.activeRoutines = routinesResult.value.map(r => ({
          ...r,
          exercises: [...(r.exercises ?? [])].sort((a, b) => a.order - b.order),
        }));
        // Auto-seleccionar si hay exactamente una rutina activa
        if (this.activeRoutines.length === 1) {
          this.selectedRoutineId = this.activeRoutines[0].id;
        }
      }

      if (exercisesResult.status === 'fulfilled') {
        this.allExercises = exercisesResult.value;
      }
    } catch {
      // non-fatal — el alumno puede registrar ejercicios libres
    }
    this.loadSavedState();
  }

  /** Rutina actualmente seleccionada (o null si ninguna) */
  private get selectedRoutine(): ActiveRoutine | null {
    if (!this.selectedRoutineId) return null;
    return this.activeRoutines.find(r => r.id === this.selectedRoutineId) ?? null;
  }

  private root(): HTMLElement {
    return document.getElementById('ws-root')!;
  }

  private drawPhase() {
    const root = this.root();
    if (!root) return;
    if (this.phase === 'idle') root.innerHTML = this.renderIdle();
    else if (this.phase === 'active') {
      root.innerHTML = this.renderActive();
      this.prefetchRoutineReferences();
    }
    else if (this.phase === 'rest') root.innerHTML = this.renderRest();
    else root.innerHTML = this.renderDone();
    this.bindEvents();
  }

  /** Precarga las referencias de los ejercicios de la rutina seleccionada (1 request batch). */
  private prefetchRoutineReferences() {
    const routine = this.selectedRoutine;
    if (!routine || routine.exercises.length === 0) return;
    this.loadReferences(routine.exercises.map(re => re.exercise.id));
  }

  // ─── STATE STORAGE ───────────────────────────────────────────────────────────
  private saveState() {
    try {
      const state = {
        phase: this.phase,
        loggedSets: this.loggedSets,
        notes: this.notes,
        startedAt: this.startedAt?.toISOString() || null,
        restDeadline: this.restDeadline,
        selectedRoutineId: this.selectedRoutineId,
        selectedRoutineExerciseId: this.selectedRoutineExerciseId,
        lastSelectedExerciseId: this.lastSelectedExerciseId,
        lastUsedByExercise: this.lastUsedByExercise,
      };
      localStorage.setItem('discipline_workout_session', JSON.stringify(state));
    } catch (e) {
      console.error('Error saving workout session state:', e);
    }
  }

  private clearSavedState() {
    try {
      localStorage.removeItem('discipline_workout_session');
    } catch (e) {
      console.error('Error clearing workout session state:', e);
    }
  }

  private loadSavedState() {
    try {
      const saved = localStorage.getItem('discipline_workout_session');
      if (saved) {
        const parsed = JSON.parse(saved);
        this.phase = parsed.phase || 'idle';
        this.loggedSets = parsed.loggedSets || [];
        this.notes = parsed.notes || '';
        this.startedAt = parsed.startedAt ? new Date(parsed.startedAt) : null;
        this.restDeadline = typeof parsed.restDeadline === 'number' ? parsed.restDeadline : null;
        this.restRemaining = this.restDeadline != null ? this.getRemainingSeconds() : 0;
        if (parsed.selectedRoutineId && this.activeRoutines.some(r => r.id === parsed.selectedRoutineId)) {
          this.selectedRoutineId = parsed.selectedRoutineId;
        }
        if (
          parsed.selectedRoutineExerciseId &&
          this.activeRoutines.some(r =>
            r.exercises.some(re => re.id === parsed.selectedRoutineExerciseId),
          )
        ) {
          this.selectedRoutineExerciseId = parsed.selectedRoutineExerciseId;
        }
        if (parsed.lastSelectedExerciseId && this.findExerciseById(parsed.lastSelectedExerciseId)) {
          this.lastSelectedExerciseId = parsed.lastSelectedExerciseId;
        }
        if (parsed.lastUsedByExercise && typeof parsed.lastUsedByExercise === 'object') {
          this.lastUsedByExercise = parsed.lastUsedByExercise;
        }
        if (this.phase === 'rest') {
          this.resumeRest();
        }
      }
    } catch (e) {
      console.error('Error loading saved workout session:', e);
    }
  }

  private resumeRest() {
    if (this.restDeadline == null) {
      this.restRemaining = 0;
      this.phase = 'active';
      this.saveState();
      return;
    }
    this.restRemaining = this.getRemainingSeconds();
    if (this.restRemaining <= 0) {
      this.restDeadline = null;
      this.restRemaining = 0;
      this.phase = 'active';
      this.saveState();
      return;
    }
    if (this.restInterval) clearInterval(this.restInterval);
    restAlert.schedule(this.restDeadline);
    this.restInterval = setInterval(() => this.tickRest(), 1000);
    this.tickRest();
  }

  private stopRestOnlyInterval() {
    if (this.restInterval) {
      clearInterval(this.restInterval);
      this.restInterval = null;
    }
  }

  // ─── IDLE ────────────────────────────────────────────────────────────────────
  private renderIdle(): string {
    const hasMultiple = this.activeRoutines.length >= 2;
    const hasNone = this.activeRoutines.length === 0;

    const routineSelectorHtml = hasMultiple
      ? `
        <div style="margin-bottom:1.5rem;text-align:left;">
          <p style="font-weight:700;margin-bottom:0.75rem;font-size:0.95rem;">
            Elegí tu rutina de referencia para esta sesión:
          </p>
          <div id="ws-routine-selector" style="display:flex;flex-direction:column;gap:0.75rem;">
            ${this.activeRoutines.map(r => `
              <label style="display:flex;align-items:flex-start;gap:0.75rem;padding:0.9rem 1rem;
                border:2px solid ${this.selectedRoutineId === r.id ? 'var(--tyris-energy)' : 'var(--border-metallic)'};
                border-radius:var(--radius-sm);cursor:pointer;transition:border-color 0.2s;background:rgba(0,0,0,0.15);">
                <input type="radio" name="ws-routine-choice" value="${r.id}"
                  ${this.selectedRoutineId === r.id ? 'checked' : ''}
                  style="margin-top:0.2rem;accent-color:var(--tyris-energy);flex-shrink:0;" />
                <div>
                  <div style="font-weight:600;margin-bottom:0.2rem;color:var(--tyris-energy);">${r.name}</div>
                  ${r.description ? `<div style="font-size:0.82rem;color:var(--text-muted);">${r.description}</div>` : ''}
                  <div style="font-size:0.78rem;color:var(--text-muted);margin-top:0.3rem;">
                    ${r.exercises.length} ejercicio${r.exercises.length !== 1 ? 's' : ''}
                  </div>
                </div>
              </label>
            `).join('')}
          </div>
          <p style="font-size:0.78rem;color:var(--text-muted);margin-top:0.6rem;">
            La rutina elegida aparecerá como acceso rápido al seleccionar ejercicios.
          </p>
        </div>`
      : hasNone
        ? `<p style="color:var(--text-muted);margin-bottom:1.5rem;font-size:0.9rem;">
            Sin rutinas activas asignadas. Podés registrar ejercicios libremente.
           </p>`
        : `<p style="color:var(--text-muted);margin-bottom:1.5rem;font-size:0.9rem;">
            Se usará <strong style="color:var(--tyris-energy);">${this.activeRoutines[0]?.name ?? 'tu rutina activa'}</strong> como guía.
            Podés añadir cualquier ejercicio libremente.
           </p>`;

    return `
      <div class="dashboard-header" style="margin-bottom:1.5rem;">
        <div class="dashboard-title">
          <h2 style="font-size:1.6rem; font-weight:800;">Comenzar Entrenamiento</h2>
          <p style="color:var(--text-muted);">Registra cada serie, descansa y persiste tu sesión automáticamente.</p>
        </div>
      </div>
      <div class="card-glass card-workout" style="max-width:560px;margin:1.5rem auto;padding:2.5rem;text-align:center;">
        <div style="margin-bottom:1rem;color:var(--tyris-energy);">
          ${getTyrisIcon('comenzar-entrenamiento', { size: 54 })}
        </div>
        <h3 style="font-size:1.5rem;margin-bottom:0.75rem;font-weight:700;color:var(--tyris-energy);">¿Listo para entrenar?</h3>
        ${routineSelectorHtml}
        <button id="ws-start" class="btn btn-student" style="width:100%;padding:1rem;font-size:1.05rem;">
          ${getTyrisIcon('comenzar', { size: 20 })}
          <span>Iniciar Sesión</span>
        </button>
      </div>
    `;
  }

  // ─── ACTIVE ──────────────────────────────────────────────────────────────────
  private renderActive(): string {
    const loggedHtml = this.renderLoggedSets();

    const routineLabel = this.selectedRoutine
      ? `<span style="font-size:0.82rem;color:var(--tyris-energy);margin-left:0.5rem;font-weight:600;display:inline-flex;align-items:center;gap:0.3rem;">${getTyrisIcon('mis-rutinas', { size: 14 })} ${this.selectedRoutine.name}</span>`
      : '';

    const lastUsed = this.lastSelectedExerciseId
      ? this.lastUsedByExercise[this.lastSelectedExerciseId]
      : null;
    const defaultWeight = lastUsed?.weight ?? 0;
    const defaultReps   = lastUsed?.reps   ?? 10;

    return `
      <div class="dashboard-header" style="margin-bottom:1.5rem;">
        <div class="dashboard-title">
          <h2 style="font-size:1.6rem; font-weight:800;">Entrenamiento en Curso ${routineLabel}</h2>
          <p style="color:var(--text-muted);">Registra cada serie. Al terminar, guarda la sesión.</p>
        </div>
        <div style="display:flex;gap:0.6rem;flex-wrap:wrap;justify-content:flex-end;">
          <button id="ws-cancel" class="btn btn-outline" style="width:auto;padding:0.6rem 1.2rem;font-size:0.85rem;border-color:rgba(229,72,77,0.4);color:var(--color-error);">
            ✕ Cancelar
          </button>
          <button id="ws-finish" class="btn btn-student" style="width:auto;padding:0.6rem 1.5rem;">
            ${getTyrisIcon('éxito', { size: 18 })}
            <span>Finalizar y Guardar</span>
          </button>
        </div>
      </div>

      <!-- Formulario de serie -->
      <div class="card-glass card-workout" style="padding:1.5rem;margin-bottom:1.5rem;">
        <h4 style="margin-bottom:1rem;font-size:1.1rem;font-weight:700;color:var(--text-main);display:flex;align-items:center;gap:0.4rem;">${getTyrisIcon('plus', { size: 16 })} Registrar Serie</h4>
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:1rem;margin-bottom:1rem;">
          <div style="grid-column:1/-1;">
            <label style="font-size:0.8rem;color:var(--text-muted);display:block;margin-bottom:0.4rem;font-weight:600;">Ejercicio</label>
            <input type="hidden" id="ws-exercise" value="${this.lastSelectedExerciseId ?? ''}" />
            <div id="ws-combobox-wrap" style="position:relative;">
              <input id="ws-exercise-search"
                type="text"
                autocomplete="off"
                placeholder="Buscar o seleccionar ejercicio..."
                value="${this.lastSelectedExerciseId ? (this.findExerciseById(this.lastSelectedExerciseId)?.name ?? '') : ''}"
                style="width:100%;padding:0.65rem 0.9rem;background:var(--bg-surface);border:1px solid var(--border-metallic);border-radius:var(--radius-sm);color:var(--text-main);font-size:0.95rem;box-sizing:border-box;" />
              <ul id="ws-exercise-dropdown"
                style="display:none;position:absolute;top:calc(100% + 4px);left:0;right:0;z-index:200;margin:0;padding:0.35rem 0;list-style:none;background:var(--bg-surface-elevated);border:1px solid var(--border-metallic);border-radius:var(--radius-sm);max-height:260px;overflow-y:auto;box-shadow:var(--shadow-lg);">
              </ul>
            </div>
            <button id="ws-technique-btn" class="btn btn-outline btn-sm"
              style="display:none;width:100%;margin-top:0.5rem;padding:0.55rem 0.9rem;font-size:0.85rem;border-color:rgba(111,190,149,0.4);color:var(--tyris-energy);justify-content:center;gap:0.4rem;">
              ${getTyrisIcon('ver-técnica', { size: 16 })}
              <span>Ver Técnica del Ejercicio</span>
            </button>
          </div>

          <!-- HISTORICAL REFERENCE -->
          <div id="ws-exercise-reference" style="grid-column:1/-1; display:none; padding:0.65rem 0.9rem; background:rgba(255,255,255,0.03); border:1px dashed var(--border-metallic); border-radius:var(--radius-sm); font-size:0.88rem; color:var(--text-muted);">
          </div>

          <div>
            <label style="font-size:0.8rem;color:var(--text-muted);display:block;margin-bottom:0.4rem;font-weight:600;">Peso (kg)</label>
            <input id="ws-weight" type="number" min="0" step="0.5" value="${defaultWeight}"
              style="width:100%;padding:0.65rem 0.9rem;background:var(--bg-surface);border:1px solid var(--border-metallic);border-radius:var(--radius-sm);color:var(--text-main);font-size:0.95rem;" />
          </div>
          <div>
            <label style="font-size:0.8rem;color:var(--text-muted);display:block;margin-bottom:0.4rem;font-weight:600;">Repeticiones</label>
            <input id="ws-reps" type="number" min="1" step="1" value="${defaultReps}"
              style="width:100%;padding:0.65rem 0.9rem;background:var(--bg-surface);border:1px solid var(--border-metallic);border-radius:var(--radius-sm);color:var(--text-main);font-size:0.95rem;" />
          </div>
          <div style="grid-column:1/-1;">
            <label style="font-size:0.8rem;color:var(--text-muted);display:block;margin-bottom:0.4rem;font-weight:600;">RIR (opcional)</label>
            <input id="ws-rir" type="number" min="0" max="10" step="1" placeholder="0 – 10"
              style="width:100%;padding:0.65rem 0.9rem;background:var(--bg-surface);border:1px solid var(--border-metallic);border-radius:var(--radius-sm);color:var(--text-main);font-size:0.95rem;" />
            <div id="ws-rir-error" style="display:none;color:var(--color-error);font-size:0.78rem;margin-top:0.35rem;">
              El RIR debe ser un número entre 0 y 10.
            </div>
          </div>
        </div>
        <div style="display:flex;gap:0.75rem;align-items:center;flex-wrap:wrap;">
          <button id="ws-log-set" class="btn btn-student" style="width:auto;padding:0.65rem 1.5rem;">
            ${getTyrisIcon('éxito', { size: 16 })}
            <span>Anotar Serie</span>
          </button>
          <span style="color:var(--text-muted);font-size:0.85rem;">luego inicia el descanso</span>
        </div>
      </div>

      <!-- GIF Technique Modal -->
      <div id="ws-gif-modal" class="modal-overlay" style="display:none;position:fixed;inset:0;background:rgba(0,0,0,0.85);z-index:1000;align-items:center;justify-content:center;backdrop-filter:blur(5px);padding:1rem;">
        <div class="modal-box card-glass" style="max-width:480px;width:100%;padding:1.25rem;display:flex;flex-direction:column;gap:0.85rem;position:relative;">
          <button id="ws-close-gif" style="position:absolute;top:0.85rem;right:0.85rem;background:none;border:none;color:var(--text-muted);font-size:1.4rem;cursor:pointer;line-height:1;">✕</button>
          <h3 id="ws-gif-title" style="margin:0;font-size:1.1rem;font-weight:700;padding-right:2rem;color:var(--tyris-energy);"></h3>
          <div style="display:flex;justify-content:center;align-items:center;background:rgba(0,0,0,0.4);border-radius:var(--radius-md);overflow:hidden;border:1px solid var(--border-metallic);">
            <img id="ws-gif-img" src="" alt="Demostración de ejercicio" style="max-width:100%;max-height:55vh;object-fit:contain;display:block;" />
          </div>
        </div>
      </div>

      <!-- Series registradas -->
      ${loggedHtml}

      <!-- Notas -->
      <div class="card-glass card-workout" style="padding:1.25rem;margin-top:1rem;">
        <label style="font-size:0.8rem;color:var(--text-muted);display:block;margin-bottom:0.4rem;font-weight:600;">Notas de la sesión (opcional)</label>
        <textarea id="ws-notes" rows="2" style="width:100%;padding:0.65rem 0.9rem;background:var(--bg-surface);border:1px solid var(--border-metallic);border-radius:var(--radius-sm);color:var(--text-main);font-size:0.9rem;resize:vertical;">${this.notes}</textarea>
      </div>
    `;
  }

  private buildExerciseItems(query: string = ''): Array<{id: string; label: string; group: string; routineExerciseId?: string}> {
    const terms = query.trim().toLowerCase().split(/\s+/).filter(t => t.length > 0);
    const matches = (name: string) => {
      const lower = name.toLowerCase();
      return terms.length === 0 || terms.every(t => lower.includes(t));
    };

    const routine = this.selectedRoutine;
    const routineExercises = routine?.exercises ?? [];
    const routineIds = new Set(routineExercises.map(r => r.exercise.id));
    const groupLabel = routine ? `Rutina: ${routine.name}` : 'Mi Rutina Activa';

    const items: Array<{id: string; label: string; group: string; routineExerciseId?: string}> = [];

    for (const re of routineExercises) {
      if (!matches(re.exercise.name)) continue;
      const weightStr = re.weight != null && re.weight > 0 ? ` @ ${re.weight}kg` : '';
      const rirStr = re.rir != null ? ` · RIR ${re.rir}` : '';
      items.push({
        id: re.exercise.id,
        label: `${re.exercise.name} (${re.sets}×${re.reps}${weightStr}${rirStr})`,
        group: groupLabel,
        routineExerciseId: re.id,
      });
    }

    for (const e of this.allExercises) {
      if (routineIds.has(e.id)) continue;
      if (!matches(e.name)) continue;
      items.push({ id: e.id, label: e.name, group: 'Catálogo General de Ejercicios' });
    }

    return items;
  }

  private renderLoggedSets(): string {
    if (this.loggedSets.length === 0) {
      return `<div class="card-glass card-workout" style="padding:1.5rem;text-align:center;color:var(--text-muted);">
        Aún no has registrado ninguna serie. ¡Empieza arriba!
      </div>`;
    }

    const grouped: Record<string, LoggedSet[]> = {};
    for (const s of this.loggedSets) {
      if (!grouped[s.exerciseName]) grouped[s.exerciseName] = [];
      grouped[s.exerciseName].push(s);
    }

    return `
      <div class="card-glass card-workout" style="padding:1.25rem;">
        <h4 style="margin-bottom:1rem;font-size:1rem;font-weight:700;color:var(--text-main);display:flex;align-items:center;gap:0.4rem;">
          ${getTyrisIcon('comenzar-entrenamiento', { size: 16 })} Series Registradas (${this.loggedSets.length})
        </h4>
        ${Object.entries(grouped).map(([name, sets]) => `
          <div style="margin-bottom:1rem;">
            <div style="font-weight:600;color:var(--tyris-energy);margin-bottom:0.5rem;">${name}</div>
            <div style="display:flex;flex-wrap:wrap;gap:0.5rem;">
              ${sets.map(s => `
                <div style="background:rgba(0,0,0,0.25);border:1px solid var(--border-metallic);border-radius:var(--radius-sm);padding:0.4rem 0.75rem;font-size:0.85rem;">
                  <span style="color:var(--text-muted);">S${s.setNumber}</span>
                  <span style="font-weight:700;margin-left:0.4rem;">${s.weight}kg × ${s.reps}</span>${s.rir != null ? `<span style="color:var(--text-muted);margin-left:0.4rem;">· RIR ${s.rir}</span>` : ''}
                  <button data-del="${this.loggedSets.indexOf(s)}" style="background:none;border:none;color:var(--color-error);cursor:pointer;margin-left:0.4rem;font-size:0.8rem;">✕</button>
                </div>
              `).join('')}
            </div>
          </div>
        `).join('')}
      </div>
    `;
  }

  // ─── REST TIMER ──────────────────────────────────────────────────────────────
  private renderRest(): string {
    return `
      <div style="display:flex;flex-direction:column;align-items:center;justify-content:center;min-height:60vh;gap:1.5rem;text-align:center;">
        <div style="color:var(--tyris-energy);">
          ${getTyrisIcon('descanso', { size: 54 })}
        </div>
        <h2 style="font-size:2rem;font-weight:800;">Descanso</h2>
        <div id="ws-timer" style="font-size:5rem;font-weight:900;color:var(--tyris-energy);font-variant-numeric:tabular-nums;">
          ${this.formatTime(this.restRemaining)}
        </div>
        <div style="display:flex;gap:1rem;flex-wrap:wrap;justify-content:center;">
          <button id="ws-rest-skip" class="btn btn-student" style="width:auto;padding:0.65rem 1.5rem;display:inline-flex;align-items:center;gap:0.4rem;">
            ${getTyrisIcon('comenzar', { size: 16 })}
            <span>Saltar Descanso</span>
          </button>
          <button id="ws-rest-add30" class="btn btn-outline" style="width:auto;padding:0.65rem 1.5rem;">
            +30s
          </button>
        </div>
        <button id="ws-cancel" style="background:none;border:none;color:rgba(229,72,77,0.6);font-size:0.82rem;cursor:pointer;text-decoration:underline;padding:0.25rem;margin-top:0.5rem;">
          Cancelar entrenamiento
        </button>
      </div>
    `;
  }

  private renderDone(): string {
    const durationSec = this.startedAt && this.finishedAt
      ? Math.round((this.finishedAt.getTime() - this.startedAt.getTime()) / 1000)
      : null;
    const durationText = durationSec != null
      ? `${Math.floor(durationSec / 60)} min ${durationSec % 60} seg`
      : '';

    return `
      <div style="display:flex;flex-direction:column;align-items:center;justify-content:center;min-height:60vh;gap:1.5rem;text-align:center;">
        <div style="color:var(--tyris-energy);">
          ${getTyrisIcon('sesion-guardada', { size: 64 })}
        </div>
        <h2 style="font-size:2rem;font-weight:800;">¡Sesión Guardada!</h2>
        <p style="color:var(--text-muted);max-width:400px;line-height:1.6;">
          Tu entrenamiento de ${this.loggedSets.length} series ha sido persistido correctamente.
        </p>
        ${durationText ? `<div style="background:rgba(111,190,149,0.12);border:1px solid var(--tyris-energy);border-radius:var(--radius-sm);padding:0.75rem 1.5rem;font-size:1rem;color:var(--tyris-energy);">
          Duración: <strong>${durationText}</strong>
        </div>` : ''}
        <div style="background:rgba(111,190,149,0.12);border:1px solid var(--tyris-energy);border-radius:var(--radius-sm);padding:0.75rem 1.5rem;font-size:0.95rem;color:var(--tyris-energy);">
          Asistencia del día registrada automáticamente.
        </div>
        <div style="display:flex;gap:1rem;flex-wrap:wrap;justify-content:center;">
          <a href="#/workout-history" class="btn btn-outline" style="width:auto;padding:0.65rem 1.5rem;">Ver Historial</a>
          <button id="ws-new" class="btn btn-student" style="width:auto;padding:0.65rem 1.5rem;">Nueva Sesión</button>
        </div>
      </div>
    `;
  }

  // ─── EVENTS ──────────────────────────────────────────────────────────────────
  private bindEvents() {
    const $ = (id: string) => document.getElementById(id);

    $('ws-start')?.addEventListener('click', () => {
      if (this.activeRoutines.length >= 2 && !this.selectedRoutineId) {
        toast.error('Seleccioná una rutina de referencia antes de iniciar.');
        return;
      }
      restAlert.unlock();
      this.startedAt = new Date();
      this.phase = 'active';
      this.saveState();
      this.drawPhase();
    });

    document.querySelectorAll('input[name="ws-routine-choice"]').forEach(radio => {
      radio.addEventListener('change', (e) => {
        this.selectedRoutineId = (e.target as HTMLInputElement).value;
        this.saveState();
        document.querySelectorAll('input[name="ws-routine-choice"]').forEach(r => {
          const label = (r as HTMLElement).closest('label');
          if (label) {
            label.style.borderColor = (r as HTMLInputElement).checked
              ? 'var(--tyris-energy)'
              : 'var(--border-metallic)';
          }
        });
      });
    });

    $('ws-log-set')?.addEventListener('click', () => this.logSet());
    $('ws-finish')?.addEventListener('click', () => this.saveSession());
    $('ws-cancel')?.addEventListener('click', () => this.cancelSession());
    $('ws-rest-skip')?.addEventListener('click', () => this.stopRest(true));

    $('ws-rest-add30')?.addEventListener('click', () => {
      if (this.restDeadline != null) {
        this.restDeadline += 30 * 1000;
        restAlert.schedule(this.restDeadline);
      }
      this.restRemaining = this.getRemainingSeconds();
      this.saveState();
      const el = document.getElementById('ws-timer');
      if (el) el.textContent = this.formatTime(this.restRemaining);
    });

    $('ws-new')?.addEventListener('click', () => {
      this.loggedSets = [];
      this.notes = '';
      this.startedAt = null;
      this.finishedAt = null;
      this.phase = 'idle';
      this.lastSelectedExerciseId = null;
      this.selectedRoutineExerciseId = null;
      this.isSaving = false;
      restAlert.cancel();
      this.clearSavedState();
      this.drawPhase();
    });

    $('ws-notes')?.addEventListener('input', (e) => {
      this.notes = (e.target as HTMLTextAreaElement).value;
      this.saveState();
    });

    document.querySelectorAll('[data-del]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const idx = parseInt((e.currentTarget as HTMLElement).dataset.del!);
        this.loggedSets.splice(idx, 1);
        const counts: Record<string, number> = {};
        for (const s of this.loggedSets) {
          counts[s.exerciseId] = (counts[s.exerciseId] || 0) + 1;
          s.setNumber = counts[s.exerciseId];
        }
        this.saveState();
        this.drawPhase();
      });
    });

    // ── COMBOBOX ──────────────────────────────────────────────────────────────
    const wsHidden    = $('ws-exercise') as HTMLInputElement | null;
    const wsSearch    = $('ws-exercise-search') as HTMLInputElement | null;
    const wsDropdown  = $('ws-exercise-dropdown') as HTMLUListElement | null;
    const techniqueBtn = $('ws-technique-btn') as HTMLButtonElement | null;

    const updateTechniqueBtn = () => {
      const ex = this.findExerciseById(wsHidden?.value);
      if (techniqueBtn) techniqueBtn.style.display = ex?.gifPath ? 'inline-flex' : 'none';
    };

    const selectExercise = (id: string, label: string, routineExerciseId?: string) => {
      if (wsHidden) wsHidden.value = id;
      if (wsSearch) wsSearch.value = label;
      if (wsDropdown) wsDropdown.style.display = 'none';
      this.lastSelectedExerciseId = id || null;
      this.selectedRoutineExerciseId = routineExerciseId ?? null;
      this.saveState();
      this.updateReferenceText();
      updateTechniqueBtn();
      if (id) this.loadReferences([id]);
      const lastUsed = this.lastUsedByExercise[id];
      if (lastUsed) {
        const weightEl = document.getElementById('ws-weight') as HTMLInputElement | null;
        const repsEl   = document.getElementById('ws-reps')   as HTMLInputElement | null;
        if (weightEl) weightEl.value = String(lastUsed.weight);
        if (repsEl)   repsEl.value   = String(lastUsed.reps);
      }
    };

    const clearSelection = () => {
      if (wsHidden) wsHidden.value = '';
      this.lastSelectedExerciseId = null;
      this.selectedRoutineExerciseId = null;
      this.saveState();
      this.updateReferenceText();
      updateTechniqueBtn();
    };

    const renderDropdown = (query: string) => {
      if (!wsDropdown) return;
      const items = this.buildExerciseItems(query);
      if (items.length === 0) {
        wsDropdown.innerHTML = `<li style="padding:0.65rem 1rem;color:var(--text-muted);font-size:0.9rem;">Sin resultados</li>`;
        wsDropdown.style.display = 'block';
        return;
      }
      let html = '';
      let lastGroup = '';
      for (const item of items) {
        if (item.group !== lastGroup) {
          html += `<li style="padding:0.35rem 0.9rem 0.2rem;font-size:0.72rem;color:var(--text-muted);font-weight:600;letter-spacing:0.05em;text-transform:uppercase;pointer-events:none;border-top:${lastGroup ? '1px solid var(--border-metallic)' : 'none'};margin-top:${lastGroup ? '0.25rem' : '0'}">${item.group}</li>`;
          lastGroup = item.group;
        }
        const isActive = item.id === this.lastSelectedExerciseId;
        const routineExerciseAttr = item.routineExerciseId
          ? ` data-routine-exercise-id="${item.routineExerciseId}"`
          : '';
        html += `<li data-id="${item.id}" data-label="${item.label.replace(/"/g, '&quot;')}"${routineExerciseAttr} style="padding:0.6rem 1rem;cursor:pointer;font-size:0.92rem;color:var(--text-main);background:${isActive ? 'rgba(111,190,149,0.15)' : 'transparent'};border-left:${isActive ? '3px solid var(--tyris-energy)' : '3px solid transparent'};transition:background 0.1s;">${item.label}</li>`;
      }
      wsDropdown.innerHTML = html;
      wsDropdown.style.display = 'block';

      wsDropdown.querySelectorAll('li[data-id]').forEach(li => {
        li.addEventListener('mousedown', (e) => {
          e.preventDefault();
          const id    = (li as HTMLElement).dataset.id!;
          const label = (li as HTMLElement).dataset.label!;
          const routineExerciseId = (li as HTMLElement).dataset.routineExerciseId;
          selectExercise(id, label, routineExerciseId);
        });
        (li as HTMLElement).addEventListener('mouseover', () => {
          (li as HTMLElement).style.background = 'rgba(255,255,255,0.06)';
        });
        (li as HTMLElement).addEventListener('mouseout', () => {
          const isActive = (li as HTMLElement).dataset.id === this.lastSelectedExerciseId;
          (li as HTMLElement).style.background = isActive ? 'rgba(111,190,149,0.15)' : 'transparent';
        });
      });
    };

    wsSearch?.addEventListener('focus', () => renderDropdown(wsSearch.value));

    wsSearch?.addEventListener('click', () => {
      wsSearch.value = '';
      renderDropdown('');
    });

    wsSearch?.addEventListener('input', () => {
      if (!wsSearch.value.trim()) clearSelection();
      renderDropdown(wsSearch.value);
    });

    wsSearch?.addEventListener('blur', () => {
      setTimeout(() => {
        if (wsDropdown) wsDropdown.style.display = 'none';
        if (wsSearch && !wsSearch.value.trim() && this.lastSelectedExerciseId) {
          const ex = this.findExerciseById(this.lastSelectedExerciseId);
          if (ex) wsSearch.value = ex.name;
        }
      }, 150);
    });

    if (wsSearch && this.lastSelectedExerciseId) {
      const ex = this.findExerciseById(this.lastSelectedExerciseId);
      if (ex) wsSearch.value = ex.name;
      else clearSelection();
    }

    this.updateReferenceText();
    updateTechniqueBtn();

    // Si se restauró una selección previa (recarga de sesión activa), cargar su referencia
    if (wsHidden?.value) {
      this.loadReferences([wsHidden.value]);
    }

    techniqueBtn?.addEventListener('click', () => {
      const id = wsHidden?.value;
      const ex = this.findExerciseById(id);
      if (!ex?.gifPath) return;
      const modal = $('ws-gif-modal');
      const title = $('ws-gif-title');
      const img   = $('ws-gif-img') as HTMLImageElement;
      if (modal && title && img) {
        title.textContent = ex.name;
        img.src = ex.gifPath;
        modal.style.display = 'flex';
      }
    });

    const closeGifModal = () => {
      const modal = $('ws-gif-modal');
      const img   = $('ws-gif-img') as HTMLImageElement;
      if (modal) modal.style.display = 'none';
      if (img)   img.src = '';
    };
    $('ws-close-gif')?.addEventListener('click', closeGifModal);
    $('ws-gif-modal')?.addEventListener('click', (e) => {
      if (e.target === e.currentTarget) closeGifModal();
    });
  }

  /** Carga referencias (última sesión + PR) para los ejercicios indicados en una sola request batch. */
  private async loadReferences(exerciseIds: string[]): Promise<void> {
    const missing = [...new Set(exerciseIds)].filter(id =>
      id && !this.referenceByExercise[id] && !this.referenceLoadingByExercise[id],
    );
    if (missing.length === 0) return;

    const wasLoading: string[] = [];
    for (const id of missing) {
      this.referenceLoadingByExercise[id] = true;
      this.referenceErrorByExercise[id] = false;
      wasLoading.push(id);
    }
    this.updateReferenceText();

    try {
      const { references } = await apiClient.get<{ references: ExerciseReference[] }>(
        '/api/workout-sessions/exercises/reference',
        { params: { exerciseIds: missing.join(',') } },
      );
      for (const ref of references) {
        this.referenceByExercise[ref.exerciseId] = ref;
      }
    } catch {
      for (const id of wasLoading) {
        this.referenceErrorByExercise[id] = true;
      }
    } finally {
      for (const id of wasLoading) {
        delete this.referenceLoadingByExercise[id];
      }
      this.updateReferenceText();
    }
  }

  private updateReferenceText() {
    const wsHidden = document.getElementById('ws-exercise') as HTMLInputElement | null;
    const refContainer = document.getElementById('ws-exercise-reference');
    if (!wsHidden || !refContainer) return;

    const exerciseId = wsHidden.value;
    if (!exerciseId) {
      refContainer.style.display = 'none';
      refContainer.innerHTML = '';
      return;
    }

    refContainer.style.display = 'block';
    const rxHtml = this.renderPrescription(exerciseId);

    if (this.referenceLoadingByExercise[exerciseId]) {
      refContainer.innerHTML = `
        <div style="display:flex;flex-direction:column;gap:0.6rem;">
          ${rxHtml}
          <div class="ws-ref-loading">
            ${getTyrisIcon('en-curso', { size: 14, style: 'animation: spin 1s linear infinite' })}
            <span>Cargando historial…</span>
          </div>
        </div>`;
      return;
    }

    if (this.referenceErrorByExercise[exerciseId]) {
      refContainer.innerHTML = `
        <div style="display:flex;flex-direction:column;gap:0.6rem;">
          ${rxHtml}
          <div class="ws-ref-empty">No se pudo cargar el historial en este momento.</div>
        </div>`;
      return;
    }

    const ref = this.referenceByExercise[exerciseId];
    if (!ref) return;

    if (!ref.lastSession && !ref.personalRecord) {
      refContainer.innerHTML = `
        <div style="display:flex;flex-direction:column;gap:0.6rem;">
          ${rxHtml}
          <div class="ws-ref-empty">
            ${getTyrisIcon('medidas', { size: 14 })}
            <span>Aún no tenés registros anteriores para este ejercicio.</span>
          </div>
        </div>`;
      return;
    }

    const lastSessionHtml = ref.lastSession
      ? this.renderLastSession(ref.lastSession)
      : '';

    const prHtml = ref.personalRecord
      ? this.renderPersonalRecord(ref.personalRecord)
      : '';

    refContainer.innerHTML = `
      <div style="display:flex;flex-direction:column;gap:0.7rem;">
        ${rxHtml}
        ${lastSessionHtml ? `${lastSessionHtml}<hr class="ws-ref-divider" />` : ''}
        ${prHtml}
      </div>`;
  }

  private renderPrescription(exerciseId: string): string {
    const routineEx = this.selectedRoutine?.exercises.find(re => re.exercise.id === exerciseId);
    const rxParts: string[] = [];
    if (routineEx) {
      rxParts.push(`${routineEx.sets}×${routineEx.reps}`);
      if (routineEx.weight != null && routineEx.weight > 0) rxParts.push(`${routineEx.weight}kg`);
      if (routineEx.rpe != null) rxParts.push(`RPE ${routineEx.rpe}`);
      if (routineEx.rir != null) rxParts.push(`RIR ${routineEx.rir}`);
    }
    if (rxParts.length === 0) return '';

    return `
      <div>
        <div class="ws-ref-title">
          ${getTyrisIcon('mis-rutinas', { size: 14, style: 'color:var(--tyris-energy)' })}
          <span>Prescripción</span>
        </div>
        <div class="ws-ref-prescription"><strong>${rxParts.join(' · ')}</strong></div>
      </div>`;
  }

  private renderLastSession(session: { date: string; sets: ExerciseReferenceSet[] }): string {
    const chipsHtml = session.sets.map(s => `
      <span class="ws-ref-set-chip">
        <strong>${this.formatWeight(s.weight)} kg × ${s.reps} reps</strong>
      </span>
    `).join('');

    return `
      <div>
        <div class="ws-ref-title" style="color:var(--tyris-energy);">
          ${getTyrisIcon('racha', { size: 14, style: 'color:var(--tyris-energy)' })}
          <span>En tu última sesión</span>
        </div>
        <div class="ws-ref-set-chips">${chipsHtml}</div>
        <div class="ws-ref-date">${this.formatDate(session.date)}</div>
      </div>`;
  }

  private renderPersonalRecord(pr: { date: string; weight: number; reps: number }): string {
    return `
      <div>
        <div class="ws-ref-title" style="color:var(--tyris-honor);">
          ${getTyrisIcon('racha', { size: 14, style: 'color:var(--tyris-honor)' })}
          <span>Tu mejor registro</span>
        </div>
        <div class="ws-ref-pr" style="font-size:0.88rem;">
          Tu PR fue el día <strong>${this.formatDate(pr.date)}</strong> e hiciste
          <strong>${this.formatWeight(pr.weight)} kg</strong> en un total de
          <strong>${pr.reps} repeticiones</strong>.
        </div>
      </div>`;
  }

  private formatWeight(weight: number): string {
    return Number.isInteger(weight) ? String(weight) : String(weight);
  }

  private formatDate(dateStr: string): string {
    const d = new Date(dateStr);
    const day = d.getDate().toString().padStart(2, '0');
    const month = (d.getMonth() + 1).toString().padStart(2, '0');
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
  }

  private findExerciseById(id: string | undefined | null): Exercise | null {
    if (!id) return null;
    const routineEx = this.selectedRoutine?.exercises.find(re => re.exercise.id === id)?.exercise;
    if (routineEx) return routineEx;
    return this.allExercises.find(e => e.id === id) ?? null;
  }

  private logSet() {
    const exerciseEl = document.getElementById('ws-exercise') as HTMLInputElement;
    const weightEl   = document.getElementById('ws-weight') as HTMLInputElement;
    const repsEl     = document.getElementById('ws-reps') as HTMLInputElement;
    const rirEl      = document.getElementById('ws-rir') as HTMLInputElement;
    const rirError   = document.getElementById('ws-rir-error') as HTMLDivElement;

    const exerciseId = exerciseEl?.value;
    const weight     = parseFloat(weightEl?.value ?? '0');
    const reps       = parseInt(repsEl?.value ?? '0');

    const rawRir = rirEl?.value.trim();
    const isEmpty = rawRir === '';
    const parsedRir = !isEmpty ? parseInt(rawRir) : NaN;
    const rirOutOfRange = !isEmpty && (isNaN(parsedRir) || parsedRir < 0 || parsedRir > 10);

    if (rirOutOfRange) {
      if (rirEl) {
        rirEl.style.borderColor = 'var(--color-error)';
        rirEl.focus();
      }
      if (rirError) rirError.style.display = 'block';
      return;
    }

    if (rirEl) rirEl.style.borderColor = 'var(--border-metallic)';
    if (rirError) rirError.style.display = 'none';

    const rir: number | null = !isEmpty ? parsedRir : null;

    if (!exerciseId) { toast.error('Selecciona un ejercicio'); return; }
    if (isNaN(reps) || reps < 1) { toast.error('Ingresa repeticiones válidas'); return; }

    const ex = this.findExerciseById(exerciseId);
    const exerciseName = ex?.name ?? exerciseId;
    const prevSets = this.loggedSets.filter(s => s.exerciseId === exerciseId).length;

    this.loggedSets.push({ exerciseId, exerciseName, setNumber: prevSets + 1, weight, reps, rir });

    this.lastSelectedExerciseId = exerciseId;
    this.lastUsedByExercise[exerciseId] = { weight, reps };

    const notesEl = document.getElementById('ws-notes') as HTMLTextAreaElement;
    if (notesEl) this.notes = notesEl.value;

    const restSec = this.resolveRestSeconds(exerciseId);
    this.startRest(restSec);
  }

  private resolveRestSeconds(exerciseId: string): number {
    const line = this.selectedRoutine?.exercises.find(
      re => re.id === this.selectedRoutineExerciseId,
    ) ?? this.selectedRoutine?.exercises.find(
      re => re.exercise.id === exerciseId,
    );

    if (line) return line.restSec ?? line.exercise.restSec ?? 120;

    return this.allExercises.find(e => e.id === exerciseId)?.restSec ?? 120;
  }

  private startRest(seconds: number) {
    if (seconds <= 0) {
      restAlert.cancel();
      return;
    }

    this.restDeadline = Date.now() + seconds * 1000;
    this.restRemaining = seconds;
    this.phase = 'rest';
    restAlert.schedule(this.restDeadline);
    this.saveState();
    this.drawPhase();

    if (this.restInterval) clearInterval(this.restInterval);
    this.restInterval = setInterval(() => this.tickRest(), 1000);
  }

  private getRemainingSeconds(): number {
    if (this.restDeadline == null) return 0;
    return Math.max(0, Math.ceil((this.restDeadline - Date.now()) / 1000));
  }

  private tickRest() {
    if (!document.getElementById('ws-root')) {
      this.stopRestOnlyInterval();
      return;
    }
    this.restRemaining = this.getRemainingSeconds();
    this.saveState();
    const el = document.getElementById('ws-timer');
    if (el) el.textContent = this.formatTime(this.restRemaining);
    if (this.restRemaining <= 0) this.stopRest();
  }

  private stopRest(cancelAudio = false) {
    if (cancelAudio) restAlert.cancel();
    if (this.restInterval) { clearInterval(this.restInterval); this.restInterval = null; }
    this.restDeadline = null;
    this.restRemaining = 0;
    this.phase = 'active';
    this.saveState();
    this.drawPhase();
  }

  private async cancelSession() {
    const hasData = this.loggedSets.length > 0;
    const confirmed = await confirmDialog({
      title: '¿Cancelar entrenamiento?',
      message: hasData
        ? `Tenés ${this.loggedSets.length} serie(s) registrada(s) que se perderán. No se guardará asistencia.`
        : 'No se guardará ningún registro ni asistencia.',
      confirmLabel: 'Sí, cancelar',
      cancelLabel: 'Seguir entrenando',
      variant: 'danger',
    });
    if (!confirmed) return;

    if (this.restInterval) { clearInterval(this.restInterval); this.restInterval = null; }
    restAlert.cancel();
    this.restDeadline = null;
    this.restRemaining = 0;

    this.loggedSets = [];
    this.notes = '';
    this.startedAt = null;
    this.finishedAt = null;
    this.lastSelectedExerciseId = null;
    this.selectedRoutineExerciseId = null;
    this.phase = 'idle';

    this.clearSavedState();
    this.drawPhase();
  }

  private async saveSession() {
    const notesEl = document.getElementById('ws-notes') as HTMLTextAreaElement;
    if (notesEl) this.notes = notesEl.value;

    if (this.loggedSets.length === 0) {
      toast.error('No has registrado ninguna serie todavía.');
      return;
    }

    if (this.isSaving) return;
    this.isSaving = true;

    const finishBtn = document.getElementById('ws-finish') as HTMLButtonElement | null;
    if (finishBtn) {
      finishBtn.disabled = true;
      finishBtn.innerHTML = `
        ${getTyrisIcon('en-curso', { size: 18, style: 'animation: spin 1s linear infinite' })}
        <span>Guardando...</span>
      `;
    }

    this.finishedAt = new Date();

    try {
      await apiClient.post('/api/workout-sessions', {
        notes: this.notes || undefined,
        startedAt: this.startedAt?.toISOString(),
        finishedAt: this.finishedAt.toISOString(),
        routineId: this.selectedRoutineId ?? undefined,
        sets: this.loggedSets.map(s => ({
          exerciseId: s.exerciseId,
          setNumber: s.setNumber,
          weight: s.weight,
          reps: s.reps,
          ...(s.rir != null ? { rir: s.rir } : {}),
        })),
      });
      this.phase = 'done';
      this.lastSelectedExerciseId = null;
      this.selectedRoutineExerciseId = null;
      restAlert.cancel();
      this.clearSavedState();
      this.drawPhase();
    } catch (err: any) {
      this.finishedAt = null;
      toast.error(err.message || 'Error al guardar la sesión.');
      if (finishBtn) {
        finishBtn.disabled = false;
        finishBtn.innerHTML = `
          ${getTyrisIcon('éxito', { size: 18 })}
          <span>Finalizar y Guardar</span>
        `;
      }
    } finally {
      this.isSaving = false;
    }
  }

  private formatTime(seconds: number): string {
    const m = Math.floor(Math.max(0, seconds) / 60).toString().padStart(2, '0');
    const s = Math.max(0, seconds % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  }
}
